import type { TypeCurveHeader } from './api-responses';

function readResources(entity: unknown): Record<string, unknown> | null {
  if (!entity || typeof entity !== 'object') {
    return null;
  }
  const resources = (entity as { resources?: unknown }).resources;
  if (!resources || typeof resources !== 'object') {
    return null;
  }
  return resources as Record<string, unknown>;
}

function readLinkHref(link: unknown): string | null {
  if (!link || typeof link !== 'object') {
    return null;
  }
  const href = (link as { href?: unknown }).href;
  return typeof href === 'string' && href.length > 0 ? href : null;
}

function readNestedResourceHref(
  resources: Record<string, unknown> | null,
  camelKey: string,
  snakeKey: string
): string | null {
  if (!resources) {
    return null;
  }
  return readLinkHref(resources[camelKey]) ?? readLinkHref(resources[snakeKey]);
}

/**
 * Collection href for listing / creating type curve workspaces (from header `resources`).
 * @param {TypeCurveHeader | null | undefined} header Type curve header JSON.
 * @return {string | null} Path such as `/type_curves/:id/workspaces`, or null.
 */
export function typeCurveHeaderWorkspacesCollectionHref(
  header: TypeCurveHeader | null | undefined
): string | null {
  return readLinkHref(readResources(header)?.workspaces);
}

/**
 * The same workspaces collection scoped to specific kinds via `?kinds=`. The backend returns one
 * flat `workspaces` array carrying every requested kind (each row naming its own `kind`), so the
 * dropdown fetches all three in one call and groups client-side.
 * @param {TypeCurveHeader | null | undefined} header Type curve header JSON.
 * @param {Array<string>} kinds Kinds to request; defaults to all three the dropdown shows.
 * @return {string | null} Path such as `/type_curve_headers/:id/workspaces?kinds=type_curve,dca,inventory`, or null.
 */
export function typeCurveHeaderWorkspacesKindsHref(
  header: TypeCurveHeader | null | undefined,
  kinds: string[] = ['type_curve', 'dca', 'inventory']
): string | null {
  const base = typeCurveHeaderWorkspacesCollectionHref(header);
  if (!base) {
    return null;
  }
  return `${base}?kinds=${kinds.join(',')}`;
}

/**
 * Events index href for the type curve header activity feed.
 * @param {TypeCurveHeader | null | undefined} header Type curve header JSON.
 * @return {string | null} Path such as `/type_curves/:id/events`, or null.
 */
export function typeCurveHeaderEventsHref(
  header: TypeCurveHeader | null | undefined
): string | null {
  return readNestedResourceHref(readResources(header), 'events', 'events');
}

/**
 * Comments create href for the type curve header.
 * @param {TypeCurveHeader | null | undefined} header Type curve header JSON.
 * @return {string | null} Path such as `/type_curves/:id/comments`, or null.
 */
export function typeCurveHeaderCommentsHref(
  header: TypeCurveHeader | null | undefined
): string | null {
  return readNestedResourceHref(readResources(header), 'comments', 'comments');
}
